import { t } from '@lingui/macro';
import { transEnum } from '@luminovo/commons';
import {
    createColumnHelper,
    MenuButton,
    MenuItem,
    SupportedFilterFn,
    Table,
    Tag,
    TanStackTable,
    useTanStackTable,
} from '@luminovo/design-system';
import { SupplierPartType, SupplierPreference } from '@luminovo/http-client';
import {
    formatSupplierAndStockLocationDTO,
    supplierOriginTranslations,
    supplierPartTypeTranslations,
    SupplierPreferenceLabel,
    supplierPreferenceTranslations,
    supplierTypeTranslations,
} from '@luminovo/sourcing-core';
import { ArrowDropDownRounded, EditRounded } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { SourcingTemplateFormValues } from './types';

type RowType = SourcingTemplateFormValues['supplierPreferences'][number];

const columnHelper = createColumnHelper<RowType>();

const columns = [
    columnHelper.text((row) => formatSupplierAndStockLocationDTO(row), {
        id: 'supplier.name',
        size: 250,
        label: () => t`Supplier name`,
    }),

    columnHelper.text('supplier.supplier_number', {
        size: 130,
        label: () => t`Supplier number`,
        cell: (item) => item.getValue() ?? '-',
    }),

    columnHelper.enum('supplier.origin', {
        size: 80,
        label: () => t`Origin`,
        getOptionLabel: (option) => transEnum(option, supplierOriginTranslations),
        cell: (item) => (
            <Tag attention="low" color={'neutral'} label={transEnum(item.getValue(), supplierOriginTranslations)} />
        ),
    }),
    columnHelper.enum('supplier.supplier_part_type', {
        size: 90,
        label: () => t`Part type`,
        options: [SupplierPartType.OffTheShelf, SupplierPartType.Custom, SupplierPartType.Pcb],
        getOptionLabel: (option) => transEnum(option, supplierPartTypeTranslations),
        cell: (item) => (
            <Tag attention="low" color={'neutral'} label={transEnum(item.getValue(), supplierPartTypeTranslations)} />
        ),
    }),
    columnHelper.enum('supplier.supplier_type', {
        size: 90,
        label: () => t`Supplier type`,
        getOptionLabel: (option) => transEnum(option, supplierTypeTranslations),
        cell: (item) => (
            <Tag attention="low" color={'neutral'} label={transEnum(item.getValue(), supplierTypeTranslations)} />
        ),
    }),
    columnHelper.enum('preference', {
        id: 'preference',
        size: 120,
        label: () => t`Preference`,
        options: [
            SupplierPreference.Preferred,
            SupplierPreference.Approved,
            SupplierPreference.NotApproved,
            SupplierPreference.Excluded,
        ],
        getOptionLabel: (option) => transEnum(option, supplierPreferenceTranslations),
        quickFilters: [
            {
                label: () => t`Preferred`,
                showCount: true,
                value: {
                    filterFn: SupportedFilterFn.equalsAny,
                    value: [SupplierPreference.Preferred],
                },
            },
            {
                label: () => t`Approved`,
                showCount: true,
                value: {
                    filterFn: SupportedFilterFn.equalsAny,
                    value: [SupplierPreference.Approved],
                },
            },
            {
                label: () => t`Not approved`,
                showCount: true,
                value: {
                    filterFn: SupportedFilterFn.equalsAny,
                    value: [SupplierPreference.NotApproved],
                },
            },
            {
                label: () => t`Excluded`,
                showCount: true,
                value: {
                    filterFn: SupportedFilterFn.equalsAny,
                    value: [SupplierPreference.Excluded],
                },
            },
        ],
        cell: ({ row }) => <RowActionButton row={row.original} />,
    }),
];

function RowActionButton({ row }: { row: RowType }): JSX.Element {
    const { setValue, control } = useFormContext<SourcingTemplateFormValues>();
    const supplierPreferences = useWatch({ control, name: `supplierPreferences` });
    const index = supplierPreferences.findIndex((supplierPreference) => supplierPreference.id === row.id);

    return (
        <MenuButton
            size={'small'}
            appearance="secondary"
            label={<SupplierPreferenceLabel preference={row.preference} endIcon={<ArrowDropDownRounded />} />}
            style={{ margin: 0 }}
        >
            <MenuItem
                label={<SupplierPreferenceLabel preference={SupplierPreference.Preferred} />}
                disabled={row.preference === SupplierPreference.Preferred}
                onClick={() =>
                    setValue(`supplierPreferences.${index}.preference`, SupplierPreference.Preferred, {
                        shouldDirty: true,
                    })
                }
            />
            <MenuItem
                label={<SupplierPreferenceLabel preference={SupplierPreference.Approved} />}
                disabled={row.preference === SupplierPreference.Approved}
                onClick={() =>
                    setValue(`supplierPreferences.${index}.preference`, SupplierPreference.Approved, {
                        shouldDirty: true,
                    })
                }
            />
            <MenuItem
                label={<SupplierPreferenceLabel preference={SupplierPreference.NotApproved} />}
                disabled={row.preference === SupplierPreference.NotApproved}
                onClick={() =>
                    setValue(`supplierPreferences.${index}.preference`, SupplierPreference.NotApproved, {
                        shouldDirty: true,
                    })
                }
            />
            <MenuItem
                label={<SupplierPreferenceLabel preference={SupplierPreference.Excluded} />}
                disabled={row.preference === SupplierPreference.Excluded}
                onClick={() =>
                    setValue(`supplierPreferences.${index}.preference`, SupplierPreference.Excluded, {
                        shouldDirty: true,
                    })
                }
            />
        </MenuButton>
    );
}

function ActionButton({ table }: { table: Table<RowType> }) {
    const { setValue, control } = useFormContext<SourcingTemplateFormValues>();
    const supplierPreferences = useWatch({ control, name: `supplierPreferences` });
    const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);

    const handleClick = (preference: SupplierPreference) => {
        const selectedIds = new Set(selectedRows.map((row) => row.id));
        setValue(
            'supplierPreferences',
            supplierPreferences.map((item) => (selectedIds.has(item.id) ? { ...item, preference } : item)),
            { shouldDirty: true },
        );
        table.setRowSelection({});
    };

    return (
        <MenuButton
            appearance="primary"
            icon={<EditRounded />}
            label={t`Change preference`}
            size={'medium'}
            disabled={selectedRows.length === 0}
            style={{ margin: 0 }}
        >
            <MenuItem
                label={t`Preferred`}
                disabled={selectedRows.every((row) => row.preference === SupplierPreference.Preferred)}
                onClick={() => handleClick(SupplierPreference.Preferred)}
            />
            <MenuItem
                label={t`Approved`}
                disabled={selectedRows.every((row) => row.preference === SupplierPreference.Approved)}
                onClick={() => handleClick(SupplierPreference.Approved)}
            />
            <MenuItem
                label={t`Not approved`}
                disabled={selectedRows.every((row) => row.preference === SupplierPreference.NotApproved)}
                onClick={() => handleClick(SupplierPreference.NotApproved)}
            />
            <MenuItem
                label={t`Excluded`}
                disabled={selectedRows.every((row) => row.preference === SupplierPreference.Excluded)}
                onClick={() => handleClick(SupplierPreference.Excluded)}
            />
        </MenuButton>
    );
}

export function SupplierPreferencesTab() {
    const { control } = useFormContext<SourcingTemplateFormValues>();
    const data = useWatch({ control, name: 'supplierPreferences' });

    const { table } = useTanStackTable({
        data,
        columns,
        enableColumnHiding: true,
        enableColumnOrdering: true,
        enableSelection: {
            enabled: true,
            getRowId: (row) => row.id,
        },
    });

    return (
        <Box height={'calc(100% - 48px)'}>
            <TanStackTable table={table} ActionButton={ActionButton} />
        </Box>
    );
}
